import { Injectable } from '@angular/core';
import { ScaniaSelectOption } from '../../components/scania-selector/scania-selector.component';

/**
 * Configuración para la conversión de datos a opciones de selector
 */
export interface SelectorConverterConfig {
  /** Propiedad a usar como value (por defecto: 'id') */
  valueField?: string;
  /** Propiedad a usar como label (por defecto: 'name') */
  labelField?: string;
  /** Propiedad a usar como disabled (opcional) */
  disabledField?: string;
  /** Propiedad a usar como icon (opcional) */
  iconField?: string;
  /** Función personalizada para determinar si está disabled */
  disabledFn?: (item: any) => boolean;
  /** Función personalizada para generar el label */
  labelFn?: (item: any) => string;
  /** Función personalizada para generar el value */
  valueFn?: (item: any) => any;
  /** Filtrar items que cumplan esta condición */
  filterFn?: (item: any) => boolean;
  /** Ordenar por esta propiedad */
  sortBy?: string;
  /** Orden descendente */
  sortDesc?: boolean;
  /** Función personalizada de ordenación */
  sortFn?: (a: any, b: any) => number;
}

@Injectable({
  providedIn: 'root'
})
export class SelectorConverterService {

  /**
   * Convierte un array de objetos a opciones de selector
   * @param data Array de objetos a convertir
   * @param config Configuración de la conversión
   * @returns Array de ScaniaSelectOption
   * 
   * @example
   * // Conversión simple con campos por defecto (id, name)
   * const options = selectorConverter.fromArray(provinces);
   * 
   * @example
   * // Conversión con campos personalizados
   * const options = selectorConverter.fromArray(items, {
   *   valueField: 'code',
   *   labelField: 'description'
   * });
   * 
   * @example
   * // Conversión con función de label personalizada
   * const options = selectorConverter.fromArray(items, {
   *   labelFn: (item) => `${item.code} - ${item.name}`
   * });
   */
  fromArray(data: any[], config: SelectorConverterConfig = {}): ScaniaSelectOption[] {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    const {
      valueField = 'id',
      labelField = 'name',
      disabledField,
      iconField,
      disabledFn,
      labelFn,
      valueFn,
      filterFn,
      sortBy,
      sortDesc = false,
      sortFn
    } = config;

    let result = data;

    // Filtrar si hay función de filtro
    if (filterFn) {
      result = result.filter(filterFn);
    }

    // Mapear a opciones
    let options: ScaniaSelectOption[] = result.map(item => {
      const option: ScaniaSelectOption = {
        value: valueFn ? valueFn(item) : this.getNestedValue(item, valueField),
        label: labelFn ? labelFn(item) : this.getNestedValue(item, labelField) || ''
      };

      // Disabled
      if (disabledFn) {
        option.disabled = disabledFn(item);
      } else if (disabledField) {
        option.disabled = !!this.getNestedValue(item, disabledField);
      }

      // Icon
      if (iconField) {
        option.icon = this.getNestedValue(item, iconField);
      }

      return option;
    });

    // Ordenar
    if (sortFn) {
      options.sort(sortFn);
    } else if (sortBy) {
      options.sort((a, b) => {
        const aVal = sortBy === 'label' ? a.label : a.value;
        const bVal = sortBy === 'label' ? b.label : b.value;
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        }
        
        return sortDesc ? (bVal - aVal) : (aVal - bVal);
      });
    }

    return options;
  }

  /**
   * Convierte un objeto clave-valor a opciones de selector
   * @param data Objeto con estructura { key: value } o { key: "label" }
   * @param useKeyAsValue Si es true, usa la clave como value; si es false, usa el valor como value
   * @returns Array de ScaniaSelectOption
   * 
   * @example
   * // Objeto { "10": "Disponible", "20": "Reservado" }
   * const options = selectorConverter.fromKeyValue(vehicleStatuses);
   * // Resultado: [{ value: "10", label: "Disponible" }, { value: "20", label: "Reservado" }]
   * 
   * @example
   * // Parsear claves a número
   * const options = selectorConverter.fromKeyValue(vehicleStatuses, true, true);
   * // Resultado: [{ value: 10, label: "Disponible" }, ...]
   */
  fromKeyValue(
    data: Record<string, string>, 
    useKeyAsValue: boolean = true,
    parseKeyAsNumber: boolean = true
  ): ScaniaSelectOption[] {
    if (!data || typeof data !== 'object') {
      return [];
    }

    return Object.entries(data).map(([key, label]) => ({
      value: useKeyAsValue ? (parseKeyAsNumber ? parseInt(key, 10) : key) : label,
      label: label
    }));
  }

  /**
   * Convierte un objeto agrupado (como provinces por país) a opciones de selector
   * @param data Objeto con estructura { groupKey: items[] }
   * @param config Configuración de la conversión
   * @param groupKey Clave del grupo específico a obtener (opcional, si no se pasa devuelve todos)
   * @returns Array de ScaniaSelectOption
   * 
   * @example
   * // Obtener provincias del país 1
   * const options = selectorConverter.fromGroupedObject(provinces, {}, '1');
   * 
   * @example
   * // Obtener todas las provincias de todos los grupos
   * const options = selectorConverter.fromGroupedObject(provinces);
   */
  fromGroupedObject(
    data: Record<string, any[]>, 
    config: SelectorConverterConfig = {},
    groupKey?: string
  ): ScaniaSelectOption[] {
    if (!data || typeof data !== 'object') {
      return [];
    }

    let items: any[];

    if (groupKey !== undefined) {
      items = data[groupKey] || [];
    } else {
      // Combinar todos los grupos
      items = ([] as any[]).concat(...Object.values(data));
    }

    return this.fromArray(items, config);
  }

  /**
   * Convierte settingValues (estructura especial del JSON) a opciones de selector
   * @param settingValues Objeto settingValues del JSON
   * @param typeCode Código del tipo (10, 20, 30, etc.)
   * @param config Configuración adicional
   * @returns Array de ScaniaSelectOption
   * 
   * @example
   * // Obtener marcas de camión (type 10)
   * const brands = selectorConverter.fromSettingValues(settingValues, 10);
   * 
   * @example
   * // Obtener modelos filtrados por marca
   * const models = selectorConverter.fromSettingValues(settingValues, 20, {
   *   filterFn: (item) => item.idBrand === 1
   * });
   */
  fromSettingValues(
    settingValues: Record<string, { typeCode: number; typeName: string; values: any[] }>,
    typeCode: number,
    config: SelectorConverterConfig = {}
  ): ScaniaSelectOption[] {
    if (!settingValues || typeof settingValues !== 'object') {
      return [];
    }

    const setting = settingValues[typeCode.toString()];
    if (!setting || !setting.values) {
      return [];
    }

    return this.fromArray(setting.values, config);
  }

  /**
   * Obtiene el nombre del tipo de un settingValue
   * @param settingValues Objeto settingValues del JSON
   * @param typeCode Código del tipo
   * @returns Nombre del tipo o undefined
   */
  getSettingTypeName(
    settingValues: Record<string, { typeCode: number; typeName: string; values: any[] }>,
    typeCode: number
  ): string | undefined {
    const setting = settingValues[typeCode.toString()];
    return setting?.typeName;
  }

  /**
   * Convierte un enum TypeScript a opciones de selector
   * @param enumObj El enum a convertir
   * @param labelMap Mapeo opcional de valores a etiquetas personalizadas
   * @returns Array de ScaniaSelectOption
   * 
   * @example
   * enum Status { Active = 1, Inactive = 2 }
   * const options = selectorConverter.fromEnum(Status, { 1: 'Activo', 2: 'Inactivo' });
   */
  fromEnum(enumObj: object, labelMap?: Record<string | number, string>): ScaniaSelectOption[] {
    const entries = Object.entries(enumObj);
    const numericKeys = entries.filter(([key]) => !isNaN(Number(key)));
    
    // Si hay claves numéricas, es un enum numérico
    if (numericKeys.length > 0) {
      return numericKeys.map(([key, value]) => ({
        value: Number(key),
        label: labelMap?.[Number(key)] || String(value)
      }));
    }

    // Enum string
    return entries.map(([key, value]) => ({
      value: value,
      label: labelMap?.[value] || key
    }));
  }

  /**
   * Crea opciones a partir de un rango numérico
   * @param start Inicio del rango
   * @param end Fin del rango (inclusive)
   * @param step Incremento (por defecto 1)
   * @param labelFn Función para generar el label (por defecto el número como string)
   * @returns Array de ScaniaSelectOption
   * 
   * @example
   * // Años del 2020 al 2030
   * const years = selectorConverter.fromRange(2020, 2030);
   * 
   * @example
   * // Meses con etiquetas personalizadas
   * const months = selectorConverter.fromRange(1, 12, 1, (n) => `Mes ${n}`);
   */
  fromRange(
    start: number, 
    end: number, 
    step: number = 1, 
    labelFn?: (n: number) => string
  ): ScaniaSelectOption[] {
    const options: ScaniaSelectOption[] = [];
    
    for (let i = start; i <= end; i += step) {
      options.push({
        value: i,
        label: labelFn ? labelFn(i) : i.toString()
      });
    }

    return options;
  }

  /**
   * Crea opciones booleanas (Sí/No)
   * @param trueLabel Etiqueta para true (por defecto 'Sí')
   * @param falseLabel Etiqueta para false (por defecto 'No')
   * @returns Array de ScaniaSelectOption
   */
  booleanOptions(trueLabel: string = 'Sí', falseLabel: string = 'No'): ScaniaSelectOption[] {
    return [
      { value: true, label: trueLabel },
      { value: false, label: falseLabel }
    ];
  }

  /**
   * Añade una opción "vacía" al inicio de las opciones
   * @param options Array de opciones existente
   * @param emptyLabel Etiqueta para la opción vacía
   * @param emptyValue Valor para la opción vacía (por defecto null)
   * @returns Nuevo array con la opción vacía al inicio
   */
  withEmptyOption(
    options: ScaniaSelectOption[], 
    emptyLabel: string = '-- Selecciona --',
    emptyValue: any = null
  ): ScaniaSelectOption[] {
    return [
      { value: emptyValue, label: emptyLabel },
      ...options
    ];
  }

  /**
   * Filtra opciones por texto de búsqueda
   * @param options Array de opciones
   * @param searchText Texto a buscar
   * @param caseSensitive Búsqueda sensible a mayúsculas (por defecto false)
   * @returns Array filtrado de opciones
   */
  filterBySearch(
    options: ScaniaSelectOption[], 
    searchText: string,
    caseSensitive: boolean = false
  ): ScaniaSelectOption[] {
    if (!searchText) {
      return options;
    }

    const search = caseSensitive ? searchText : searchText.toLowerCase();
    
    return options.filter(option => {
      const label = caseSensitive ? option.label : option.label.toLowerCase();
      return label.includes(search);
    });
  }

  /**
   * Encuentra una opción por su valor
   * @param options Array de opciones
   * @param value Valor a buscar
   * @returns La opción encontrada o undefined
   */
  findByValue(options: ScaniaSelectOption[], value: any): ScaniaSelectOption | undefined {
    return options.find(opt => opt.value === value);
  }

  /**
   * Obtiene los labels de múltiples valores seleccionados
   * @param options Array de opciones
   * @param values Array de valores seleccionados
   * @param separator Separador para unir los labels (por defecto ', ')
   * @returns String con los labels unidos
   */
  getLabelsForValues(
    options: ScaniaSelectOption[], 
    values: any[],
    separator: string = ', '
  ): string {
    if (!values || !Array.isArray(values)) {
      return '';
    }

    return values
      .map(val => this.findByValue(options, val)?.label)
      .filter(Boolean)
      .join(separator);
  }

  /**
   * Obtiene un valor anidado de un objeto usando notación de punto
   * @param obj Objeto del que extraer el valor
   * @param path Ruta al valor (ej: 'address.city.name')
   * @returns El valor encontrado o undefined
   */
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }
}
